// src/components/Dashboard/AiAdvisor.jsx
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { aiService } from '../../services/api';
import './AiAdvisor.css';

const AiAdvisor = ({ field, analysisData, onClose }) => {
    const { t, language } = useLanguage();
    const [loading, setLoading] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [error, setError] = useState(null);
    const [cropKnowledge, setCropKnowledge] = useState(null);
    const [activeIndex, setActiveIndex] = useState('NDVI'); // NDVI, NDRE, or NDMI

    const handleAnalyze = async () => {
        if (!field || !analysisData) {
            setError(t('ai.missing_data'));
            return;
        }

        setLoading(true);
        setError(null);
        setAnalysis(null);

        try {
            const result = await aiService.analyze(field, analysisData, language);

            if (result.success) {
                setAnalysis(result.analysis);
                setCropKnowledge(result.cropKnowledgeUsed);
            } else {
                setError(result.error || t('ai.analysis_failed'));
            }
        } catch (err) {
            console.error('AI Analysis error:', err);
            setError(err.response?.data?.error || err.message || t('ai.analysis_failed'));
        } finally {
            setLoading(false);
        }
    };

    // Get current index data based on active tab
    const getCurrentIndexData = () => {
        switch (activeIndex) {
            case 'NDVI':
                return {
                    mean: analysisData?.ndvi,
                    min: analysisData?.minNdvi,
                    max: analysisData?.maxNdvi,
                    std: analysisData?.stdNdvi,
                    imagePath: analysisData?.ndviImagePath,
                    label: 'NDVI',
                    icon: '🍃',
                    fullName: 'Bitki Sağlığı'
                };
            case 'NDRE':
                return {
                    mean: analysisData?.ndre,
                    min: analysisData?.minNdre,
                    max: analysisData?.maxNdre,
                    std: analysisData?.stdNdre,
                    imagePath: analysisData?.ndreImagePath,
                    label: 'NDRE',
                    icon: '🌿',
                    fullName: 'Klorofil/Azot'
                };
            case 'NDMI':
                return {
                    mean: analysisData?.ndmi,
                    min: analysisData?.minNdmi,
                    max: analysisData?.maxNdmi,
                    std: analysisData?.stdNdmi,
                    imagePath: analysisData?.ndmiImagePath,
                    label: 'NDMI',
                    icon: '💧',
                    fullName: 'Nem İndeksi'
                };
            default:
                return null;
        }
    };

    const currentData = getCurrentIndexData();

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    // Simple markdown-like rendering
    const renderAnalysis = (text) => {
        if (!text) return null;

        return text.split('\n').map((line, index) => {
            // Headers
            if (line.startsWith('## ')) {
                return <h3 key={index} className="ai-heading">{line.replace('## ', '')}</h3>;
            }
            if (line.startsWith('### ')) {
                return <h4 key={index} className="ai-subheading">{line.replace('### ', '')}</h4>;
            }
            // Bold text with **
            if (line.includes('**')) {
                const parts = line.split(/\*\*(.*?)\*\*/g);
                return (
                    <p key={index}>
                        {parts.map((part, i) =>
                            i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                        )}
                    </p>
                );
            }
            // List items
            if (line.startsWith('- ') || line.startsWith('• ')) {
                return <li key={index}>{line.replace(/^[-•] /, '')}</li>;
            }
            // Numbered lists
            if (/^\d+\. /.test(line)) {
                return <li key={index}>{line.replace(/^\d+\. /, '')}</li>;
            }
            // Empty lines
            if (line.trim() === '') {
                return <br key={index} />;
            }
            // Regular paragraph
            return <p key={index}>{line}</p>;
        });
    };

    return (
        <div className="ai-advisor-overlay" onClick={onClose}>
            <div className="ai-advisor-modal" onClick={e => e.stopPropagation()}>
                <div className="ai-advisor-header">
                    <div className="ai-title">
                        <span className="ai-icon">🤖</span>
                        <h2>{t('ai.title')}</h2>
                    </div>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="ai-modal-content">
                    <div className="ai-field-info">
                        <div className="field-badge">
                            <span className="field-name">{field?.name}</span>
                            <span className="field-crop">{field?.cropType || t('field.not_specified')}</span>
                        </div>
                        {analysisData?.date && (
                            <div className="analysis-date">
                                <span className="date-icon">📅</span>
                                <span className="date-text">{formatDate(analysisData.date)}</span>
                            </div>
                        )}
                    </div>

                    {/* Index Tabs */}
                    <div className="index-tabs">
                        <button
                            className={`index-tab ${activeIndex === 'NDVI' ? 'active' : ''}`}
                            onClick={() => setActiveIndex('NDVI')}
                        >
                            🍃 NDVI
                        </button>
                        <button
                            className={`index-tab ${activeIndex === 'NDRE' ? 'active' : ''}`}
                            onClick={() => setActiveIndex('NDRE')}
                        >
                            🌿 NDRE
                        </button>
                        <button
                            className={`index-tab ${activeIndex === 'NDMI' ? 'active' : ''}`}
                            onClick={() => setActiveIndex('NDMI')}
                        >
                            💧 NDMI
                        </button>
                    </div>

                    {/* Map Preview */}
                    {currentData?.imagePath && (
                        <div className="map-preview">
                            <img
                                src={currentData.imagePath}
                                alt={`${currentData.label} Map`}
                                className="map-image"
                            />
                        </div>
                    )}

                    {/* Enhanced Values Display */}
                    <div className="enhanced-values">
                        <div className="index-info-header">
                            <span className="index-icon">{currentData?.icon}</span>
                            <span className="index-name">{currentData?.label} - {currentData?.fullName}</span>
                        </div>

                        <div className="value-grid">
                            <div className="value-box primary">
                                <span className="value-label">Average</span>
                                <span className="value-number">{currentData?.mean?.toFixed(3) || '-'}</span>
                            </div>
                            <div className="value-box">
                                <span className="value-label">Minimum</span>
                                <span className="value-number small">{currentData?.min?.toFixed(3) || '-'}</span>
                                <span className="value-hint">Low areas</span>
                            </div>
                            <div className="value-box">
                                <span className="value-label">Maximum</span>
                                <span className="value-number small">{currentData?.max?.toFixed(3) || '-'}</span>
                                <span className="value-hint">High areas</span>
                            </div>
                        </div>

                        {/* Heterogeneity indicator */}
                        {activeIndex === 'NDVI' && analysisData?.stdNdvi !== undefined && (
                            <div className="heterogeneity-indicator">
                                <span className="het-label">⚡ Heterojenlik:</span>
                                <span
                                    className="het-value"
                                    style={{ color: (analysisData.stdNdvi > 0.1) ? '#e53e3e' : '#38a169' }}
                                >
                                    {analysisData.stdNdvi > 0.1 ? 'Yüksek' : 'Düşük'}
                                </span>
                                <span className="het-hint">
                                    {analysisData.stdNdvi > 0.1
                                        ? '(Tarlada gelişim farkları var)'
                                        : '(Tarla homojen)'}
                                </span>
                            </div>
                        )}
                    </div>

                    {!analysis && !loading && (
                        <div className="ai-start-section">
                            <p className="ai-description">{t('ai.description')}</p>
                            <button
                                className="analyze-btn"
                                onClick={handleAnalyze}
                                disabled={loading}
                            >
                                🔍 {t('ai.analyze_button')}
                            </button>
                        </div>
                    )}

                    {loading && (
                        <div className="ai-loading">
                            <div className="loading-spinner"></div>
                            <p>{t('ai.analyzing')}</p>
                            <p className="loading-hint">{t('ai.analyzing_hint')}</p>
                        </div>
                    )}

                    {error && (
                        <div className="ai-error">
                            <span className="error-icon">⚠️</span>
                            <p>{error}</p>
                            <button className="retry-btn" onClick={handleAnalyze}>
                                🔄 {t('ai.retry')}
                            </button>
                        </div>
                    )}

                    {analysis && (
                        <div className="ai-result">
                            {cropKnowledge && (
                                <div className="knowledge-badge">
                                    📚 {t('ai.knowledge_used')}: <strong>{cropKnowledge}</strong>
                                </div>
                            )}
                            <div className="analysis-content">
                                {renderAnalysis(analysis)}
                            </div>
                            <div className="ai-actions">
                                <button className="reanalyze-btn" onClick={handleAnalyze}>
                                    🔄 {t('ai.reanalyze')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AiAdvisor;
